import { format } from 'date-fns';
import { getIntl, t } from './getTranslations';
import { isNewDesignCopy } from './newDesignCopy';
import { DEFAULT_DATE_FORMAT } from './parseDate';

/** A time of day: the new design's locale writes it, classic its own 24-hour `HH:mm`. */
export const formatTimeOfDay = (value: Date | string | number): string =>
    isNewDesignCopy()
        ? getIntl().formatTime(value, { hour: '2-digit', minute: '2-digit' })
        : format(new Date(value), 'HH:mm');

export const isDateEarlierThanToday = (value: string) => {
    const comparableDate = new Date(format(new Date(value), DEFAULT_DATE_FORMAT));
    const today = new Date(format(new Date(), DEFAULT_DATE_FORMAT));
    return comparableDate < today;
};

/**
 * A task's calendar event, from the backend's UTC string, in local days and times. Given the company's date format,
 * the new design writes the days in it and each period as one message; classic writes `yyyy-MM-dd`.
 */
export const formatCalendarEventForDisplay = (
    utcDisplayString: string | null | undefined,
    isAllDay: boolean | null | undefined,
    dateFormat?: string
): { text: string; isAllDay: boolean } => {
    if (!utcDisplayString) {
        return { text: '-', isAllDay: false };
    }

    const utcMatch = utcDisplayString.match(
        /^(\d{4}-\d{2}-\d{2})\s+until\s+(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})-(\d{2}:\d{2})\s+UTC$/
    );
    if (!utcMatch) {
        return { text: utcDisplayString, isAllDay: false };
    }

    const [, startDate, endDate, startTime, endTime] = utcMatch;
    const newDesignFormat = isNewDesignCopy() ? dateFormat : undefined;
    const formatLocalDate = (date: Date) => format(date, newDesignFormat ?? DEFAULT_DATE_FORMAT);

    if (isAllDay) {
        const start = formatLocalDate(new Date(`${startDate}T00:00:00`));
        if (startDate === endDate) {
            return { text: start, isAllDay: true };
        }
        const end = formatLocalDate(new Date(`${endDate}T00:00:00`));
        return { text: newDesignFormat ? t('date-period', { start, end }) : `${start} - ${end}`, isAllDay: true };
    }

    const startUtc = new Date(`${startDate}T${startTime}:00Z`);
    const endUtc = new Date(`${endDate}T${endTime}:00Z`);

    const localStartDate = formatLocalDate(startUtc);
    const localEndDate = formatLocalDate(endUtc);
    const localStartTime = formatTimeOfDay(startUtc);
    const localEndTime = formatTimeOfDay(endUtc);

    if (newDesignFormat) {
        const start = t('date-time', { date: localStartDate, time: localStartTime });
        const end = t('date-time', { date: localEndDate, time: localEndTime });
        return { text: t('tasks.table.calendar-event-until', { start, end }), isAllDay: false };
    }
    return { text: `${localStartDate} ${localStartTime} until ${localEndDate} ${localEndTime}`, isAllDay: false };
};
